/**
 * Carries a setup begun on smartfi.ph through the sign-in detour.
 *
 * The landing's "Download & Setup" wizard saves what the visitor typed (board,
 * vendo name, WiFi name) on the cloud and sends them here as
 * app.smartfi.ph/?setup=<id>. Sign-in wipes router state and the two sites are
 * different origins, so the id rides sessionStorage exactly the way a pending
 * seller does (utils/sellerReturn): written on the sign-in screen, read once
 * by the device list, gone with the tab.
 */
import { CATALOG_ID_BY_CHOOSER_CARD } from '@/utils/deviceCatalogBoard';

const KEY = 'smartfi.setupDraft';
/** A Mongo id and nothing else — this is the only thing ever read off the URL. */
const DRAFT_ID = /^[a-f0-9]{24}$/;

export interface SetupDraft {
   id: string;
   /** Device catalog id, e.g. 'rt2980' or 'mikrotik'. */
   board: string;
   variant: 'cloud' | 'hybrid';
   name: string;
   ssid: string;
   /** Typed on smartfi.ph from the router page, stored without the dash. */
   claimCode?: string;
   expiresAt: string;
}

export function rememberSetupDraft(id: string): void {
   try {
      if (DRAFT_ID.test(id)) sessionStorage.setItem(KEY, id);
   } catch {
      // Private-mode Safari throws on write; the operator then registers by hand.
   }
}

/** Reads the pending draft id and clears it — one wizard per note. */
export function takeSetupDraft(): string | null {
   try {
      const id = sessionStorage.getItem(KEY);
      if (id) sessionStorage.removeItem(KEY);
      return id && DRAFT_ID.test(id) ? id : null;
   } catch {
      return null;
   }
}

/** Is a draft waiting, without consuming it (the post-login redirect asks). */
export function hasSetupDraft(): boolean {
   try {
      return DRAFT_ID.test(sessionStorage.getItem(KEY) ?? '');
   } catch {
      return false;
   }
}

/** The id on the sign-in URL, or null when absent or malformed. */
export function readSetupDraftFromUrl(): string | null {
   try {
      const id = new URLSearchParams(window.location.search).get('setup');
      return id && DRAFT_ID.test(id) ? id : null;
   } catch {
      return null;
   }
}

/**
 * The register wizard's "Choose your device" card for a catalog id, so a
 * draft can skip the picker. Unknown → null, and the picker is shown.
 */
export function chooserCardForCatalogId(catalogId: string): string | null {
   for (const [card, id] of Object.entries(CATALOG_ID_BY_CHOOSER_CARD)) {
      if (id === catalogId) return card;
   }
   return null;
}
